import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExternalLink, Clock, ChevronDown, Lock, Star } from 'lucide-react';
import { auth } from '../firebase';
import { apiHeaders } from '../utils/api';
import { usePlan } from '../App';
import { NEWSLETTERS } from './MesNewsletters';
import MultiDropdownSelect from '../components/MultiDropdownSelect';
import { SOURCE_LOGO } from './MesAlertes';
import {
  cleanLocation, extractCity, getContrat,
  getOfferBadge, formatRelative,
  buildVilleOptions, buildContratOptions,
} from '../utils/offerUtils';
import { isIDF } from '../utils/idfCommunes';
import { getCityGroup } from '../utils/cityGroups';

const API = 'https://aglomera.com/api';

interface Offre {
  id: number;
  title: string;
  company: string;
  location: string;
  url: string;
  date: string;
  source: string;
  remuneration: string;
  type_contrat_detail: string;
  is_new: boolean;
}

interface AbonnementDetail {
  id: number;
  newsletter_slug: string;
  localisation: string;
  types_contrat: string[];
}

const NewsletterDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { plan } = usePlan();
  const newsletter = NEWSLETTERS.find(n => n.slug === slug);

  const [abonnement, setAbonnement] = useState<AbonnementDetail | null>(null);
  const [offres, setOffres] = useState<Offre[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [villesFiltres, setVillesFiltres] = useState<string[]>([]);
  const [contratsFiltres, setContratsFiltres] = useState<string[]>([]);
  const [showFiltres, setShowFiltres] = useState(false);

  // Favoris
  const [favoriUrls, setFavoriUrls] = useState<Set<string>>(new Set());
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [justSavedId, setJustSavedId] = useState<number | null>(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    (async () => {
      fetch(`${API}/favoris/`, { headers: await apiHeaders() })
        .then(r => r.ok ? r.json() : [])
        .then((data: { url: string }[]) => setFavoriUrls(new Set(data.map(f => f.url))));
    })();
  }, []);

  const loadAbonnement = useCallback(async () => {
    const user = auth.currentUser;
    if (!user || !slug) return;
    try {
      const res = await fetch(`${API}/newsletters/abonnements/`, { headers: await apiHeaders() });
      if (res.ok) {
        const data = await res.json();
        const found = data.find((a: AbonnementDetail) => a.newsletter_slug === slug);
        setAbonnement(found || null);
      }
    } finally { setLoading(false); }
  }, [slug]);

  const loadOffres = useCallback(async () => {
    const user = auth.currentUser;
    if (!user || !abonnement) return;
    const res = await fetch(`${API}/newsletters/${abonnement.id}/offres/`, { headers: await apiHeaders() });
    if (res.ok) {
      const data = await res.json();
      setOffres(data.offres || []);
    }
  }, [abonnement]);

  useEffect(() => { loadAbonnement(); }, [loadAbonnement]);
  useEffect(() => { if (abonnement) loadOffres(); }, [abonnement, loadOffres]);

  const handleSabonner = async () => {
    const user = auth.currentUser;
    if (!user || !slug) return;
    setSubscribing(true);
    try {
      const res = await fetch(`${API}/newsletters/abonnements/`, {
        method: 'POST',
        headers: await apiHeaders(),
        body: JSON.stringify({ newsletter_slug: slug, localisation: '', types_contrat: [] }),
      });
      if (res.ok) await loadAbonnement();
    } finally { setSubscribing(false); }
  };

  const handleToggleFavori = async (offre: Offre) => {
    const user = auth.currentUser;
    if (!user) return;
    setTogglingId(offre.id);
    try {
      const res = await fetch(`${API}/favoris/toggle/`, {
        method: 'POST',
        headers: await apiHeaders(),
        body: JSON.stringify({ offre_id: offre.id }),
      });
      if (res.ok) {
        const data = await res.json();
        setFavoriUrls(prev => {
          const next = new Set(prev);
          if (data.favori) {
            next.add(offre.url);
            setJustSavedId(offre.id);
            setTimeout(() => setJustSavedId(null), 800);
          } else { next.delete(offre.url); }
          return next;
        });
      }
    } finally { setTogglingId(null); }
  };

  if (!newsletter) return <div className="p-8 text-gray-500">Newsletter introuvable.</div>;

  const isPro = plan === 'pro' || plan === 'premium';

  const offresFiltered = offres.filter(o => {
    if (villesFiltres.length > 0) {
      const city = extractCity(o.location || '');
      const match = villesFiltres.some(v => {
        if (v === '__IDF__') return isIDF(city);
        const group = getCityGroup(city);
        if (group && group.value === v) return true;
        return v === city;
      });
      if (!match) return false;
    }
    if (contratsFiltres.length > 0 && !contratsFiltres.includes(getContrat(o))) return false;
    return true;
  });

  // Le backend renvoie uniquement les offres accessibles (sélection gratuite ou tout pour pro)
  const offresVisibles = offresFiltered;
  const villeOptions = buildVilleOptions(offres);
  const contratOptions = buildContratOptions(offres);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="relative overflow-hidden" style={{ minHeight: 160 }}>
        <img src={newsletter.image} alt={newsletter.nom} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `${newsletter.couleur}d0` }} />
        <div className="relative z-10 px-8 py-8">
          <button onClick={() => navigate('/newsletters')}
            className="text-xs text-white opacity-70 hover:opacity-100 mb-4 flex items-center gap-1 transition-opacity">
            ← Mes newsletters
          </button>
          <h1 className="text-2xl font-bold text-white drop-shadow">{newsletter.nom}</h1>
          <p className="text-sm text-white opacity-90 mt-1 drop-shadow">{newsletter.tagline}</p>
          <div className="flex flex-wrap gap-1.5 mt-4">
            {newsletter.keywords.map(kw => (
              <span key={kw} className="text-xs font-medium px-2.5 py-1 rounded-full bg-white bg-opacity-20 text-white">{kw}</span>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-6 h-6 rounded-full border-2 animate-spin"
            style={{ borderColor: newsletter.couleur, borderTopColor: 'transparent' }} />
        </div>
      ) : !abonnement ? (
        <div className="p-8 flex flex-col items-center text-center gap-4">
          <p className="text-gray-600 text-sm">Vous n'êtes pas encore abonné à cette newsletter.</p>
          <button onClick={handleSabonner} disabled={subscribing}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60 hover:opacity-90 transition-all"
            style={{ background: newsletter.couleur }}>
            {subscribing ? 'Abonnement...' : "S'abonner"}
          </button>
        </div>
      ) : (
        <div className="px-8 py-6">
          {/* Barre d'actions */}
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">
                {offres.length} offre{offres.length > 1 ? 's' : ''}
              </span>
              {!isPro && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full text-gray-500"
                  style={{ background: '#f3f4f6' }}>
                  Sélection gratuite · Plan Pro pour tout voir
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowFiltres(f => !f)}
                className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl border-2 transition-all"
                style={{
                  color: (villesFiltres.length || contratsFiltres.length) ? newsletter.couleur : '#6b7280',
                  borderColor: (villesFiltres.length || contratsFiltres.length) ? newsletter.couleur : '#e5e7eb',
                }}>
                Filtres
                {(villesFiltres.length + contratsFiltres.length) > 0 && (
                  <span className="w-4 h-4 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                    style={{ background: newsletter.couleur }}>
                    {villesFiltres.length + contratsFiltres.length}
                  </span>
                )}
                <ChevronDown size={13} className={showFiltres ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>
            </div>
          </div>

          {/* Filtres */}
          {showFiltres && (
            <div className="mb-5 p-4 rounded-2xl border-2 bg-white"
              style={{ borderColor: `${newsletter.couleur}30` }}>
              <div className="flex flex-wrap gap-4">
                <MultiDropdownSelect
                  label="Localisation"
                  values={villesFiltres}
                  onChange={setVillesFiltres}
                  options={villeOptions}
                  placeholder="Toutes les villes"
                  accentColor={newsletter.couleur}
                />
                <MultiDropdownSelect
                  label="Type de contrat"
                  values={contratsFiltres}
                  onChange={setContratsFiltres}
                  options={contratOptions}
                  placeholder="Tous les types"
                  accentColor={newsletter.couleur}
                />
              </div>
              {(villesFiltres.length > 0 || contratsFiltres.length > 0) && (
                <button onClick={() => { setVillesFiltres([]); setContratsFiltres([]); }}
                  className="mt-3 text-xs font-medium underline" style={{ color: newsletter.couleur }}>
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          )}

          {/* Offres */}
          {offres.length === 0 ? (
            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-12 text-center text-gray-500 text-sm">
              Aucune offre pour l'instant.
            </div>
          ) : offresFiltered.length === 0 ? (
            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-8 text-center text-gray-500 text-sm">
              Aucune offre pour ces filtres.
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {offresVisibles.map(offre => {
                const badge = getOfferBadge(offre, SOURCE_LOGO);
                const contratDetecte = getContrat(offre);
                const locClean = cleanLocation(offre.location);
                const isFavori = favoriUrls.has(offre.url);
                const justSaved = justSavedId === offre.id;

                return (
                  <div key={offre.id}
                    className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-4 hover:border-gray-200 hover:shadow-sm transition-all">
                    <div className="w-10 h-10 rounded-xl border border-gray-100 bg-gray-50 flex items-center justify-center flex-shrink-0 p-1.5">
                      {badge.type === 'logo' ? (
                        <img src={badge.logo} alt={badge.label}
                          className="w-full h-full object-contain"
                          onError={e => {
                            const t = e.currentTarget as HTMLImageElement;
                            t.style.display = 'none';
                            const p = t.parentElement;
                            const init = badge.label.replace(/[_-]/g,' ').split(/\s+/).filter(Boolean).slice(0,3).map((w:string)=>w[0]).join('').toUpperCase()||'??';
                            if (p) { const s = document.createElement('span'); s.style.cssText='font-size:9px;font-weight:700;color:#6b7280'; s.textContent=init; p.appendChild(s); } // L5
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: '0.03em' }}>
                          {badge.text}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 text-sm leading-snug truncate max-w-[480px]">{offre.title}</h3>
                        {offre.is_new && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                            style={{ background: '#dcfce7', color: '#16a34a' }}>Nouveau</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        {offre.company || '—'}
                        {locClean ? ` · ${locClean}` : ''}
                        {badge.label && badge.label !== 'Inconnu' ? ` · via ${badge.label}` : ''}
                      </p>
                      {contratDetecte !== 'Non spécifié' && (
                        <span className="inline-block text-[10px] mt-1 px-2 py-0.5 rounded-full font-semibold"
                          style={{ background: `${newsletter.couleur}15`, color: newsletter.couleur }}>
                          {contratDetecte}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {offre.date && (
                        <div className="flex items-center gap-1 text-[11px] text-gray-400">
                          <Clock size={11} />
                          {formatRelative(offre.date)}
                        </div>
                      )}
                      <button onClick={() => handleToggleFavori(offre)}
                        disabled={togglingId === offre.id}
                        title={isFavori ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                        className="p-1.5 rounded-xl"
                        style={{
                          color: isFavori ? '#f59e0b' : '#d1d5db',
                          background: justSaved ? '#fef3c7' : 'transparent',
                          transform: justSaved ? 'scale(1.3)' : 'scale(1)',
                          transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        }}>
                        <Star size={16} fill={isFavori ? '#f59e0b' : 'none'} stroke={isFavori ? '#f59e0b' : '#d1d5db'} />
                      </button>
                      {offre.url && (
                        <a href={offre.url} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 rounded-xl hover:bg-gray-50 text-gray-400 hover:text-gray-700 transition-all">
                          <ExternalLink size={15} />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}

              {!isPro && (
                <div className="mt-3 rounded-2xl border border-gray-100 px-5 py-4 text-center"
                  style={{ background: '#f9fafb' }}>
                  <Lock size={16} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-xs text-gray-500 mb-3">
                    Passez au plan Pro pour accéder à toutes les offres en temps réel
                  </p>
                  <button onClick={() => navigate('/abonnement')}
                    className="px-5 py-2 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-all"
                    style={{ background: newsletter.couleur }}>
                    Voir les plans
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsletterDetail;
